import { AdaptedGroup, GroupEdition, GroupEditionErrors } from "../models";
import { getUsed } from "./groupCapacityStats";

const numberOr = (value: number | null | undefined, fallback: number): number =>
  value == null || Number.isNaN(value) ? fallback : value;
const orInf = (value: number | null | undefined) => numberOr(value, Infinity);
const orMinInf = (value: number | null | undefined) => numberOr(value, -Infinity);
const orZero = (value: number | null | undefined) => numberOr(value, 0);

/*
 *  *** - *** - *** - *** -
 * *** RULES DEFINITION ***
 *  *** - *** - *** - *** -
 *
 * General view (`isSubgroup` = false):
 *  Hards:
 *    - capacity should be <= calculated capacity (against `calculatedCapacity`)
 *    - capacity should be >= current occupancy
 *      (against `max(getUsed(group), externalStats.enrollmentCount, enrollmentsCount)`)
 *    - capacity can't be augmented on inactive groups (against `capacity`)
 *
 * Subgroups (`isSubgroup` = true):
 *  Softs:
 *    - capacity should be >= 80% max efficiency (against `calculatedConfigMinCapacity`)
 *    - capacity should be <= 100% max efficiency (against `configMaxCapacity`)
 *    - capacity edited on CRN without assigned classrooms (against `effectiveMaxCapacity`)
 *  Hards:
 *    - capacity should be >= current occupancy
 *      (against `max(getUsed(group), externalStats.enrollmentCount, enrollmentsCount)`)
 *    - capacity should be <= smaller CRN's classroom (against `effectiveMaxCapacity`)
 *    - capacity can't be augmented on inactive groups (against `capacity`)
 */

/**
 * Returns true if the `capacity` is greater than the `configMaxCapacity`.
 *
 * @param group AdaptedGroup
 * @param edition GroupEdition
 *
 * @return boolean
 */
export const validateCapacityGTMaxEfficiency = (
  group: AdaptedGroup,
  edition: GroupEdition,
): boolean => {
  if (edition?.capacity == null) return false;
  const capacity = orZero(edition?.capacity);
  const threshold = orInf(group?.groupCapacity?.configMaxCapacity);
  return capacity > threshold;
};

/**
 * Returns true if the `capacity` is greater than `groupCapacity.effectiveMaxCapacity`.
 *
 * @param group AdaptedGroup
 * @param edition GroupEdition
 *
 * @return boolean
 */
export const validateCapacityGTSmallerClassrroom = (
  group: AdaptedGroup,
  edition: GroupEdition,
): boolean => {
  if (edition?.capacity == null) return false;
  const capacity = orZero(edition?.capacity);
  const threshold = orInf(group?.groupCapacity?.effectiveMaxCapacity);
  return capacity > threshold;
};

/**
 * Returns true if the `capacity` is greater than `groupCapacity.calculatedCapacity`.
 * A `null` calculated capacity doesn't trigger the error, while `0` does.
 *
 * @param group AdaptedGroup
 * @param edition GroupEdition
 *
 * @return boolean
 */
export const validateCapacityGTCalculatedCapacity = (
  group: AdaptedGroup,
  edition: GroupEdition,
): boolean => {
  if (edition?.capacity == null) return false;
  const capacity = orZero(edition?.capacity);
  const threshold = orInf(group?.groupCapacity?.calculatedCapacity);
  return capacity > threshold;
};

/**
 * Returns true if the `capacity` was edited and the
 * `groupCapacity?.effectiveMaxCapacity` is `null`.
 *
 * @param group AdaptedGroup
 * @param edition GroupEdition
 *
 * @return boolean
 */
export const validateCapacityChangeOnCrnWithoutClassrooms = (
  group: AdaptedGroup,
  edition: GroupEdition,
): boolean => {
  return edition?.capacity != null && group?.groupCapacity?.effectiveMaxCapacity == null;
};

/**
 * Returns true if the `capacity` is less than the group's `groupCapacity.calculatedConfigMinCapacity`.
 *
 * @param group AdaptedGroup
 * @param edition GroupEdition
 *
 * @return boolean
 */
export const validateCapacityLT80MaxEfficiency = (
  group: AdaptedGroup,
  edition: GroupEdition,
): boolean => {
  if (edition?.capacity == null) return false;
  const capacity = orZero(edition?.capacity);
  const threshold = orMinInf(group?.groupCapacity?.calculatedConfigMinCapacity);
  return capacity < threshold;
};

/**
 * Returns `true` when the edited `capacity` would leave the group below its
 * current occupancy.
 *
 * The threshold is the **maximum** between the slots reported as used by the
 * shared `getUsed` helper (the same source of truth the donut/UI uses) and the
 * formal enrollment counters (`externalStats.enrollmentCount`, `enrollmentsCount`).
 * Taking the max guarantees we never let the user reduce capacity below what
 * the rest of the UI is showing as occupied — including cases where the
 * formal enrollment counters are `0` but `usedCapacity` is not (e.g. internal
 * holds reported by the external system).
 *
 * Returns `false` when no edition was made or when the new capacity is greater
 * than or equal to the resolved threshold.
 *
 * @param group   The group being edited.
 * @param edition The pending edition for that group.
 *
 * @returns `true` when the edition violates the occupancy floor (hard error).
 */
export const validateCapacityLTEnrollments = (
  group: AdaptedGroup,
  edition: GroupEdition,
): boolean => {
  if (edition?.capacity == null) return false;
  const capacity = edition.capacity;
  const threshold = Math.max(
    getUsed(group) ?? 0,
    group?.externalStats?.enrollmentCount ?? 0,
    group?.enrollmentsCount ?? 0,
  );
  return capacity < threshold;
};

/**
 * Returns true if the group isn't `active` and the user is trying to augment
 * the group's capacity.
 *
 * @param group AdaptedGroup
 * @param edition GroupEdition
 *
 * @return boolean
 */
export const validateCapacityAugmentedForInactive = (
  group: AdaptedGroup,
  edition: GroupEdition,
) => {
  if (edition?.capacity == null || group?.isActive) return false;

  const capacity = orZero(edition?.capacity);
  const threshold = orInf(group?.capacity);
  return capacity > threshold;
};

/**
 * Validate the **editions** made on the given `group`
 * If the group is not editable the validations will not be performed and an
 * empty object will be returned immediately.
 * If no edition was made, no error will be triggered.
 * On the general view (`isSubgroup` = false) the only upper limit is the
 * group's `calculatedCapacity`.
 *
 * @param group AdaptedGroup
 * @param edition GroupEdition
 * @param isSubgroup boolean
 *
 * @return GroupEditionErrors
 */
const validateGroupEdition = (
  group: AdaptedGroup,
  edition: GroupEdition,
  isSubgroup: boolean,
): GroupEditionErrors => {
  if (!group?.isEditable?.allowed) return {};

  const errors: GroupEditionErrors = isSubgroup
    ? {
        CapacityGTMaxEfficiency: validateCapacityGTMaxEfficiency(group, edition),
        CapacityLT80MaxEfficiency: validateCapacityLT80MaxEfficiency(group, edition),
        CapacityGTSmallerClassroom: validateCapacityGTSmallerClassrroom(group, edition),
        CapacityChangeOnCrnWithoutClassrooms: validateCapacityChangeOnCrnWithoutClassrooms(
          group,
          edition,
        ),
        CapacityLTEnrollments: validateCapacityLTEnrollments(group, edition),
        CapacityAugmentedForInactive: validateCapacityAugmentedForInactive(group, edition),
      }
    : {
        CapacityGTCalculatedCapacity: validateCapacityGTCalculatedCapacity(group, edition),
        CapacityLTEnrollments: validateCapacityLTEnrollments(group, edition),
        CapacityAugmentedForInactive: validateCapacityAugmentedForInactive(group, edition),
      };

  if (errors.CapacityChangeOnCrnWithoutClassrooms) {
    delete errors.CapacityGTSmallerClassroom;
  }

  if (isSubgroup && group?.referentType === "CAMPUS") {
    delete errors.CapacityGTMaxEfficiency;
    delete errors.CapacityLT80MaxEfficiency;
  }

  return Object.fromEntries(
    Object.entries(errors).filter(([, triggered]) => triggered !== false),
  ) as GroupEditionErrors;
};

export default validateGroupEdition;
